import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import type { CommercialService, UpdateCommercialService } from '../models';
import { API_BUSINESS_URL } from '@/utils';

const commercialServicesService = createApi({
  reducerPath: 'commercialServicesService',
  baseQuery: createBaseQueryWithReauth(
    `${API_BUSINESS_URL}/commercial-service`
  ),
  tagTypes: [
    'CommercialServices',
    'ServiceWithOperator',
    'ServiceWithOperators',
  ],
  endpoints: (builder) => ({
    getCommercialServices: builder.query<
      PaginatedResponse<CommercialService>,
      {
        paginated: PaginatedRequest;
        search?: string;
      }
    >({
      query: ({ paginated, search }) => ({
        url: '',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && search.trim() !== '' && { name: search.trim() }),
        },
      }),
      providesTags: ['CommercialServices'],
    }),
    getCommercialServiceById: builder.query<
      CommercialService,
      { commercialServiceId: string }
    >({
      query: ({ commercialServiceId }) => ({
        url: `/${commercialServiceId}`,
      }),
      providesTags: (_result, _error, { commercialServiceId }) => [
        { type: 'CommercialServices' as const, id: commercialServiceId },
        'CommercialServices',
      ],
    }),
    updateCommercialService: builder.mutation<
      CommercialService,
      { commercialServiceId: string; data: UpdateCommercialService }
    >({
      query: ({ commercialServiceId, data }) => ({
        url: `/${commercialServiceId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { commercialServiceId }) => [
        { type: 'CommercialServices' as const, id: commercialServiceId },
        'CommercialServices',
        'ServiceWithOperator',
        'ServiceWithOperators',
      ],
    }),
  }),
});

export default commercialServicesService;
export const {
  useGetCommercialServicesQuery,
  useGetCommercialServiceByIdQuery,
  useUpdateCommercialServiceMutation,
} = commercialServicesService;
